import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Shield, CheckCircle, AlertTriangle, ExternalLink } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-US', { dateStyle: 'long', timeStyle: 'short' });
}

export default function Verify() {
  const { certificateId } = useParams();
  const [cert, setCert] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${BACKEND}/api/verify/${certificateId}`)
      .then(r => r.json())
      .then(data => {
        if (data.detail) setError(data.detail);
        else setCert(data);
      })
      .catch(() => setError('Failed to load certificate'))
      .finally(() => setLoading(false));
  }, [certificateId]);

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-blue-700 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Shield size={22} className="text-blue-700" />
            <span className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit' }}>SignatureFlow</span>
          </div>
          <p className="text-sm text-slate-500">Document Verification Portal</p>
        </div>

        {error ? (
          <div className="bg-white border border-red-200 rounded-sm p-8 text-center" data-testid="verify-error">
            <AlertTriangle size={40} className="mx-auto mb-4 text-red-400" />
            <h2 className="text-lg font-semibold text-slate-900 mb-2">Certificate Not Found</h2>
            <p className="text-slate-500 text-sm">{error}</p>
          </div>
        ) : (
          <div className="space-y-5" data-testid="verify-certificate">
            {/* Status Banner */}
            <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-5 flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                <CheckCircle size={24} className="text-emerald-600" />
              </div>
              <div>
                <h2 className="font-semibold text-emerald-900 text-lg" style={{ fontFamily: 'Outfit' }}>Document Verified</h2>
                <p className="text-emerald-700 text-sm">This document's authenticity and integrity have been verified.</p>
              </div>
            </div>

            {/* Document Info */}
            <div className="bg-white border border-slate-200 rounded-sm p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-4 border-b border-slate-100 pb-3">Document Information</h3>
              <dl className="space-y-3 text-sm">
                {[
                  ['Document', cert.document_name],
                  ['Envelope', cert.envelope_title],
                  ['Completed', formatDate(cert.completed_at)],
                  ['Sent By', cert.owner_name],
                  ['Certificate ID', certificateId],
                ].map(([label, value]) => (
                  <div key={label} className="flex items-start gap-4">
                    <dt className="text-slate-500 w-28 flex-shrink-0">{label}</dt>
                    <dd className={`text-slate-900 ${label === 'Certificate ID' ? 'font-mono text-xs break-all' : ''}`}>{value || '—'}</dd>
                  </div>
                ))}
              </dl>
            </div>

            {/* Signers */}
            {cert.signers?.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-sm p-5">
                <h3 className="text-sm font-semibold text-slate-900 mb-4 border-b border-slate-100 pb-3">Signatures</h3>
                <div className="space-y-4">
                  {cert.signers.map((signer, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 bg-slate-50 rounded-sm" data-testid={`signer-${i}`}>
                      <div className="w-9 h-9 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <CheckCircle size={16} className="text-emerald-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 text-sm">{signer.name}</p>
                        {signer.verified_email ? (
                          <p className="text-xs text-slate-600 flex items-center gap-1.5 mt-0.5" data-testid={`signer-verified-email-${i}`}>
                            <CheckCircle size={13} className="text-emerald-600 flex-shrink-0" />
                            <span>{signer.verified_email}</span>
                            <span className="text-emerald-700 font-medium bg-emerald-50 px-1.5 py-0.5 rounded-sm">Verified</span>
                          </p>
                        ) : (
                          <p className="text-xs text-slate-400 mt-0.5">Email not verified</p>
                        )}
                        <p className="text-xs text-slate-400 mt-0.5">Signed: {formatDate(signer.signed_at)} · IP: {signer.ip_address || '—'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Integrity Hash */}
            <div className="bg-white border border-slate-200 rounded-sm p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-3 border-b border-slate-100 pb-3">Document Integrity</h3>
              <div className="bg-slate-900 rounded-sm p-4 font-mono text-xs text-emerald-400 break-all" data-testid="document-hash">
                SHA-256: {cert.hash}
              </div>
              <p className="text-xs text-slate-400 mt-2">This cryptographic hash proves the document has not been modified after signing.</p>
            </div>

            {/* Audit Events */}
            {cert.audit_events?.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-sm p-5">
                <h3 className="text-sm font-semibold text-slate-900 mb-3 border-b border-slate-100 pb-3">Audit Trail (last {cert.audit_events.length} events)</h3>
                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                  {cert.audit_events.map((log, i) => (
                    <div key={i} className="flex items-center gap-3 text-xs py-1">
                      <span className="text-slate-400 w-36 flex-shrink-0 whitespace-nowrap">{formatDate(log.timestamp)}</span>
                      <span className="bg-slate-100 text-slate-700 font-mono px-1.5 py-0.5 rounded">{log.event}</span>
                      <span className="text-slate-400">{log.ip}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <p className="text-center text-xs text-slate-400">
              Certificate issued by <span className="font-semibold text-slate-500">SignatureFlow</span> ·{' '}
              <a href="/" className="text-blue-600 hover:underline">signatureflow.com</a>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
